import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CreateRewardModal({ listId, reward, onClose, onCreated }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(reward?.title || '');
  const [costPoints, setCostPoints] = useState(reward?.cost_points || 10);
  const [costPointsInput, setCostPointsInput] = useState(String(reward?.cost_points || 10));
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    
    const finalCostPoints = costPointsInput === '' ? 1 : parseInt(costPointsInput);
    if (finalCostPoints < 1) {
      toast.error(t('messages.minCostError'));
      return;
    }

    setLoading(true);
    try {
      if (reward) {
        await axios.put(
          `${API}/rewards/${reward.reward_id}`,
          { title, cost_points: finalCostPoints },
          { withCredentials: true }
        );
        toast.success(t('messages.rewardUpdated'));
      } else {
        await axios.post(
          `${API}/rewards`,
          { list_id: listId, title, cost_points: finalCostPoints },
          { withCredentials: true }
        );
        toast.success(t('messages.rewardCreated'));
      }
      onCreated();
    } catch (error) {
      console.error('Error saving reward:', error);
      toast.error(t('messages.errorSavingReward'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50" onClick={onClose}>
      <div
        className="bg-white dark:bg-zinc-800 border-t-2 sm:border-2 border-[#18181B] dark:border-zinc-600 rounded-t-2xl sm:rounded-2xl hard-shadow p-4 sm:p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="create-reward-modal"
      >
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white">
            {reward ? t('modals.editReward') : t('modals.newReward')}
          </h2>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 sm:p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5 text-zinc-900 dark:text-white" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-bold mb-1.5 sm:mb-2 text-zinc-900 dark:text-white">{t('modals.rewardTitle')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('modals.rewardTitlePlaceholder')}
              className="w-full h-10 sm:h-12 rounded-lg sm:rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 sm:px-4 text-sm sm:text-base text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
              data-testid="reward-title-input"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-bold mb-1.5 sm:mb-2 text-zinc-900 dark:text-white">
              {t('modals.costInPoints')} <span className="font-mono text-[#FFC247]">{costPointsInput || '0'}</span>
            </label>
            <input
              type="number"
              min="1"
              value={costPointsInput}
              onChange={(e) => {
                setCostPointsInput(e.target.value);
                const val = parseInt(e.target.value);
                if (!isNaN(val) && val >= 1) {
                  setCostPoints(val);
                }
              }}
              className="w-full h-10 sm:h-12 rounded-lg sm:rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 sm:px-4 text-sm sm:text-base text-zinc-900 dark:text-white focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
              data-testid="cost-input"
            />
            <p className="text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              {t('modals.costDescription', { points: costPointsInput || '0' })}
            </p>
          </div>

          <div className="flex gap-2 sm:gap-3 pt-2 sm:pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white font-bold py-2.5 sm:py-3 px-4 sm:px-6 text-sm sm:text-base hover:bg-zinc-100 dark:hover:bg-zinc-600 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all"
              data-testid="cancel-btn"
            >
              {t('modals.cancel')}
            </button>
            <button
              type="submit"
              disabled={!title.trim() || loading || costPointsInput === ''}
              className="flex-1 rounded-full border-2 border-[#18181B] bg-[#18181B] text-white font-bold py-2.5 sm:py-3 px-4 sm:px-6 text-sm sm:text-base hover:bg-[#333] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="save-btn"
            >
              {loading ? t('modals.saving') : reward ? t('modals.update') : t('modals.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
